import { Alert, Anchor, Button, Container, Paper, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertTriangle, Info } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { navigateAfterLogin } from '../../utils/postLoginNavigate'

export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // If the axios client redirected us here with a reason, show a banner.
  const redirectBanner = useMemo(() => {
    const reason = searchParams.get('reason')
    const message = searchParams.get('message')
    if (!reason) return null
    if (reason === 'session-revoked') {
      return {
        color: 'yellow' as const,
        title: 'Session ended',
        message:
          message ||
          'Your session was ended because this account signed in from another location.',
      }
    }
    return null
  }, [searchParams])

  const [conflictMessage, setConflictMessage] = useState<string | null>(null)

  const form = useForm({
    initialValues: { identifier: '', password: '' },
    validate: {
      identifier: (v) => (v.trim().length >= 2 ? null : 'Enter your email or username'),
      password: (v) => (v.length >= 6 ? null : 'Password must be at least 6 characters'),
    },
  })

  const handleSubmit = async (values: typeof form.values) => {
    setConflictMessage(null)
    try {
      const user = await login(values.identifier.trim(), values.password)
      navigateAfterLogin(user, navigate)
    } catch (err: any) {
      const status = err.response?.status
      const detail: string =
        err.response?.data?.detail || 'Invalid email, username, or password'

      if (status === 409) {
        // Single-session conflict: surface a prominent, non-dismissing banner
        // on the login form itself rather than a transient toast.
        setConflictMessage(detail)
        return
      }

      notifications.show({
        title: 'Login failed',
        message: detail,
        color: 'red',
      })
    }
  }

  return (
    <Container size={420} my={80}>
      <Title ta="center" style={{ color: 'var(--sat-text)' }}>Welcome back</Title>
      <Text c="dimmed" size="sm" ta="center" mt={5}>
        Don't have an account?{' '}
        <Anchor size="sm" onClick={() => navigate('/register')}>Register</Anchor>
      </Text>

      <Paper withBorder shadow="md" p={30} mt={30} radius="md" bg="var(--sat-bg)">
        {redirectBanner && (
          <Alert
            mb="md"
            color={redirectBanner.color}
            icon={<Info size={18} />}
            title={redirectBanner.title}
            variant="light"
          >
            {redirectBanner.message}
          </Alert>
        )}

        {conflictMessage && (
          <Alert
            mb="md"
            color="orange"
            icon={<AlertTriangle size={18} />}
            title="You are already signed on"
            variant="light"
            withCloseButton
            onClose={() => setConflictMessage(null)}
          >
            {conflictMessage}
          </Alert>
        )}

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              label="Email or username"
              placeholder="you@example.com or jsmith"
              required
              autoComplete="username"
              {...form.getInputProps('identifier')}
            />
            <PasswordInput label="Password" placeholder="Your password" required {...form.getInputProps('password')} />
            <Text ta="right" size="sm" mt={-8}>
              <Anchor size="sm" c="dimmed" onClick={() => navigate('/forgot-password')}>Forgot password?</Anchor>
            </Text>
            <Button fullWidth mt="xs" type="submit" color="grapefruit">Sign in</Button>
          </Stack>
        </form>
      </Paper>
    </Container>
  )
}
